import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  CampaignClient,
  SortField,
  SortOrder,
  GroupBy,
} from '@/types/appointments';
import type { CustomerStatsCacheItem } from '@/types/crm';

export type { CustomerStatsCacheItem };
export type CampaignSegment = 'lapsed' | 'vip' | 'debtor';

export interface CampaignsState {
  // State
  clients: CampaignClient[];
  loading: boolean;
  loadingPhase: 'idle' | 'retention' | 'customers' | 'stats';
  loadedCount: number;
  totalCount: number;
  activeCampaignSegment: CampaignSegment;
  searchQuery: string;
  sortBy: SortField;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  processedIds: string[];
  hideProcessed: boolean;
  statsCache: Record<string, CustomerStatsCacheItem>;
  statsQueueRunning: boolean;
  statsProgress: { current: number; total: number };

  // Actions
  setClients: (items: CampaignClient[]) => void;
  updateClient: (id: string, updates: Partial<CampaignClient>) => void;
  updateClientsBatch: (updatesMap: Record<string, Partial<CampaignClient>>) => void;
  setLoading: (loading: boolean) => void;
  setLoadingProgress: (phase: 'idle' | 'retention' | 'customers' | 'stats', loaded: number, total: number) => void;
  setActiveSegment: (segment: CampaignSegment) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: SortField) => void;
  setSortOrder: (sortOrder: SortOrder) => void;
  setGroupBy: (groupBy: GroupBy) => void;
  setHideProcessed: (hide: boolean) => void;
  markProcessed: (id: string) => void;
  unmarkProcessed: (id: string) => void;
  isProcessed: (id: string) => boolean;
  clearProcessed: () => void;
  updateStatsCache: (id: string, item: CustomerStatsCacheItem) => void;
  setStatsQueueRunning: (running: boolean) => void;
  setStatsProgress: (current: number, total: number) => void;
  clearStatsCache: () => void;
}

export const useCampaignsStore = create<CampaignsState>()(
  devtools(
    persist(
      (set, get) => ({
        clients: [],
        loading: false,
        loadingPhase: 'idle',
        loadedCount: 0,
        totalCount: 0,
        activeCampaignSegment: 'lapsed',
        searchQuery: '',
        sortBy: 'daysAbsent',
        sortOrder: 'desc',
        groupBy: 'none',
        processedIds: [],
        hideProcessed: false,
        statsCache: {},
        statsQueueRunning: false,
        statsProgress: { current: 0, total: 0 },

        setClients: (clients) => set({ clients }),
        updateClient: (id, updates) => {
          set((state) => ({
            clients: state.clients.map((c) => (c.id === id || c.customerId === id ? { ...c, ...updates } : c)),
          }));
        },
        updateClientsBatch: (updatesMap) => {
          set((state) => ({
            clients: state.clients.map((c) => {
              const u = updatesMap[c.id] || updatesMap[c.customerId];
              return u ? { ...c, ...u } : c;
            }),
          }));
        },
        setLoading: (loading) => set({ loading }),
        setLoadingProgress: (loadingPhase, loadedCount, totalCount) => set({ loadingPhase, loadedCount, totalCount }),
        setActiveSegment: (activeCampaignSegment) => set({ activeCampaignSegment }),
        setSearchQuery: (searchQuery) => set({ searchQuery }),
        setSortBy: (sortBy) => set({ sortBy }),
        setSortOrder: (sortOrder) => set({ sortOrder }),
        setGroupBy: (groupBy) => set({ groupBy }),
        setHideProcessed: (hideProcessed) => set({ hideProcessed }),

        markProcessed: (id) => {
          set((state) => {
            if (state.processedIds.includes(id)) return state;
            return { processedIds: [...state.processedIds, id] };
          });
        },

        unmarkProcessed: (id) => {
          set((state) => ({
            processedIds: state.processedIds.filter((pId) => pId !== id),
          }));
        },

        isProcessed: (id) => {
          return get().processedIds.includes(id);
        },

        clearProcessed: () => {
          set({ processedIds: [] });
        },

        updateStatsCache: (id, item) => {
          set((state) => ({
            statsCache: {
              ...state.statsCache,
              [id]: item,
            },
          }));
        },

        setStatsQueueRunning: (statsQueueRunning) => set({ statsQueueRunning }),

        setStatsProgress: (current, total) => set({ statsProgress: { current, total } }),

        clearStatsCache: () => set({ statsCache: {} }),
      }),
      {
        name: 'fresha-campaigns',
        partialize: (state) => ({
          processedIds: state.processedIds,
          hideProcessed: state.hideProcessed,
          activeCampaignSegment: state.activeCampaignSegment,
        }),
      }
    ),
    { name: 'CampaignsStore' }
  )
);
